import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import FilterSelect from "./FilterSelect";
import DateRangeSelect, { type DateRange } from "./DateRangeSelect";
import { Accounts, Config, Deals } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";

export interface TimelineFilters {
  ownerUserId: string | null;
  accountId: string | null;
  dealId: string | null;
  date: DateRange;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared filter defaults colocated with the bar; fast-refresh cost is acceptable.
export const EMPTY_FILTERS: TimelineFilters = {
  ownerUserId: null,
  accountId: null,
  dealId: null,
  date: { from: null, to: null },
};

interface Props {
  filters: TimelineFilters;
  onChange: (f: TimelineFilters) => void;
}

/**
 * The Timeline filter row: Owner (admins only) / Account / Deal / Date, plus a
 * Clear affordance when any filter is active. Sources dropdown options from
 * existing endpoints (each RLS-scoped); owns no feed fetching.
 */
export default function TimelineFilterBar({ filters, onChange }: Props) {
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const ownersQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners(), enabled: isAdmin });
  const accountsQuery = useQuery({ queryKey: ["accounts", ""], queryFn: () => Accounts.list() });
  const dealsQuery = useQuery({ queryKey: ["deals", { all: true }], queryFn: () => Deals.list() });

  const active =
    filters.ownerUserId !== null ||
    filters.accountId !== null ||
    filters.dealId !== null ||
    filters.date.from !== null ||
    filters.date.to !== null;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-4">
      {isAdmin && (
        <FilterSelect
          label="Owner"
          value={filters.ownerUserId}
          options={(ownersQuery.data ?? [])
            .filter((o) => o.is_active)
            .map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => onChange({ ...filters, ownerUserId: v })}
          searchable
        />
      )}
      <FilterSelect
        label="Account"
        value={filters.accountId}
        options={(accountsQuery.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
        onChange={(v) => onChange({ ...filters, accountId: v })}
        searchable
      />
      <FilterSelect
        label="Deal"
        value={filters.dealId}
        options={(dealsQuery.data ?? []).map((d) => ({ value: d.id, label: d.lead }))}
        onChange={(v) => onChange({ ...filters, dealId: v })}
        searchable
      />
      <DateRangeSelect value={filters.date} onChange={(d) => onChange({ ...filters, date: d })} />
      {active && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTERS)}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-mut-300 hover:text-mut-100 hover:bg-fg/[0.04] transition"
        >
          <X className="w-3.5 h-3.5" /> Clear
        </button>
      )}
    </div>
  );
}
